class ArticleToc extends HTMLElement {
  connectedCallback() {
    this.targetSelector = this.dataset.target || '.article-with-toc__content';
    this.levels = (this.dataset.levels || 'h2,h3')
      .split(',')
      .map((s) => s.trim().toLowerCase())
      .filter(Boolean);

    const target = document.querySelector(this.targetSelector);
    if (!target) {
      this.hideHost();
      return;
    }

    this.headings = Array.from(target.querySelectorAll(this.levels.join(',')));
    if (!this.headings.length) {
      this.hideHost();
      return;
    }

    this.assignIds();
    this.render();
    this.setupActiveTracking();
  }

  hideHost() {
    const details = this.closest('details');
    if (details) details.hidden = true;
    const aside = this.closest('.article-with-toc__toc');
    if (aside && !aside.querySelector('.article-toc__cta')) {
      aside.hidden = true;
    }
  }

  slugify(text) {
    return (
      text
        .toString()
        .toLowerCase()
        .trim()
        .normalize('NFD')
        .replace(/[̀-ͯ]/g, '')
        .replace(/[^a-z0-9\s-]/g, '')
        .replace(/\s+/g, '-')
        .replace(/-+/g, '-')
        .replace(/^-+|-+$/g, '') || 'section'
    );
  }

  assignIds() {
    const used = new Set();
    document.querySelectorAll('[id]').forEach((el) => used.add(el.id));
    this.headings.forEach((h) => {
      if (!h.id) {
        const base = this.slugify(h.textContent);
        let id = base;
        let i = 2;
        while (used.has(id)) id = `${base}-${i++}`;
        h.id = id;
        used.add(id);
      }
    });
  }

  buildTree() {
    const root = { children: [] };
    const stack = [{ depth: 0, node: root }];
    this.headings.forEach((h) => {
      const depth = this.levels.indexOf(h.tagName.toLowerCase()) + 1;
      const item = { heading: h, children: [] };
      while (stack.length > 1 && stack[stack.length - 1].depth >= depth) stack.pop();
      stack[stack.length - 1].node.children.push(item);
      stack.push({ depth, node: item });
    });
    return root.children;
  }

  buildList(items) {
    if (!items.length) return null;
    const ul = document.createElement('ul');
    ul.className = 'article-toc__list';
    items.forEach((item) => {
      const li = document.createElement('li');
      li.className = 'article-toc__item';
      const a = document.createElement('a');
      a.className = 'article-toc__link';
      a.href = `#${item.heading.id}`;
      a.textContent = item.heading.textContent.trim();
      a.dataset.tocLink = item.heading.id;
      li.appendChild(a);
      const sub = this.buildList(item.children);
      if (sub) li.appendChild(sub);
      ul.appendChild(li);
    });
    return ul;
  }

  render() {
    const list = this.buildList(this.buildTree());
    const container = this.querySelector('.article-toc__list-container');
    const target = container || this;
    target.innerHTML = '';
    if (list) target.appendChild(list);
  }

  setupActiveTracking() {
    if (!('IntersectionObserver' in window)) return;
    const links = new Map();
    this.querySelectorAll('[data-toc-link]').forEach((a) => links.set(a.dataset.tocLink, a));

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          const link = links.get(entry.target.id);
          if (!link) return;
          if (entry.isIntersecting) {
            this.querySelectorAll('.article-toc__link.is-active').forEach((l) =>
              l.classList.remove('is-active')
            );
            link.classList.add('is-active');
          }
        });
      },
      { rootMargin: '-15% 0px -70% 0px', threshold: 0 }
    );

    this.headings.forEach((h) => observer.observe(h));
  }
}

if (!customElements.get('article-toc')) {
  customElements.define('article-toc', ArticleToc);
}
